import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import { 
  ArrowLeft, Clock, User, ThumbsUp, Award, Flag, 
  Reply, Edit, Trash, Plus, MessageCircle 
} from 'lucide-react';
import './Tavern.css';

// Mock data for threads and posts
const MOCK_THREADS_DATA = {
  '101': {
    id: '101',
    title: 'Welcome to ChoiceRealms',
    content: 'Welcome to the official forums for ChoiceRealms! This is where you can discuss all aspects of the game, share your experiences, and connect with other players.\n\nPlease remember to follow our community guidelines and be respectful to other players.',
    author: 'GameMaster',
    createdAt: '2023-09-01T10:00:00Z',
    lastActivity: '2023-09-15T14:30:00Z',
    viewCount: 352,
    categoryId: '1',
    categoryName: 'Announcements',
    isPinned: true,
    isLocked: false
  },
  '201': {
    id: '201',
    title: 'What class is everyone playing?',
    content: 'I\'m curious to know what classes people are choosing. I started with a Paladin but thinking about making a Wizard next. What are your favorite classes and why?',
    author: 'AdventurerJohn',
    createdAt: '2023-09-10T09:45:00Z',
    lastActivity: '2023-09-14T09:45:00Z',
    viewCount: 128,
    categoryId: '2',
    categoryName: 'General Discussion',
    isPinned: false,
    isLocked: false
  }
};

const MOCK_POSTS = {
  '101': [
    {
      id: '1001',
      threadId: '101',
      content: 'Welcome to the official forums for ChoiceRealms! This is where you can discuss all aspects of the game, share your experiences, and connect with other players.\n\nPlease remember to follow our community guidelines and be respectful to other players.',
      author: 'GameMaster',
      createdAt: '2023-09-01T10:00:00Z',
      isEdited: false,
      reactions: {
        LIKE: 15,
        HELPFUL: 8,
        THANKS: 10
      }
    },
    {
      id: '1002',
      threadId: '101',
      content: 'Thanks for creating this amazing game! I\'ve been enjoying it immensely so far.',
      author: 'NewPlayer',
      createdAt: '2023-09-01T10:30:00Z',
      isEdited: false,
      reactions: {
        LIKE: 5
      }
    },
    {
      id: '1003',
      threadId: '101',
      content: 'I love the character creation system. So many options!',
      author: 'CharacterEnthusiast',
      createdAt: '2023-09-02T15:45:00Z',
      isEdited: true,
      reactions: {
        LIKE: 3,
        HELPFUL: 1
      }
    },
    {
      id: '1004',
      threadId: '101',
      content: 'The graphics are beautiful, and the story is engaging. Looking forward to seeing how the game evolves!',
      author: 'GameReviewer',
      createdAt: '2023-09-03T09:15:00Z',
      isEdited: false,
      reactions: {
        LIKE: 7,
        HELPFUL: 2
      }
    },
    {
      id: '1005',
      threadId: '101',
      content: 'Just started playing yesterday and I\'m already hooked. The world feels so alive!',
      author: 'FirstTimer',
      createdAt: '2023-09-05T20:30:00Z',
      isEdited: false,
      reactions: {
        LIKE: 4
      }
    }
  ],
  '201': [
    {
      id: '2001',
      threadId: '201',
      content: 'I\'m curious to know what classes people are choosing. I started with a Paladin but thinking about making a Wizard next. What are your favorite classes and why?',
      author: 'AdventurerJohn',
      createdAt: '2023-09-10T09:45:00Z',
      isEdited: false,
      reactions: {
        LIKE: 8,
        HELPFUL: 3
      }
    },
    {
      id: '2002',
      threadId: '201',
      content: 'I\'ve been playing a Rogue and loving the stealth mechanics. Great for solo play!',
      author: 'ShadowStalker',
      createdAt: '2023-09-10T10:15:00Z',
      isEdited: false,
      reactions: {
        LIKE: 5
      }
    },
    {
      id: '2003',
      threadId: '201',
      content: 'Wizard all the way! The spell variety makes combat so interesting, and I love how different spells are useful in different situations.',
      author: 'MageSupreme',
      createdAt: '2023-09-11T14:30:00Z',
      isEdited: false,
      reactions: {
        LIKE: 7,
        HELPFUL: 2
      }
    },
    {
      id: '2004',
      threadId: '201',
      content: 'I\'m playing a Cleric and enjoying the balance between combat and support abilities. Plus, the divine intervention ability has saved my party more than once!',
      author: 'HolyHealer',
      createdAt: '2023-09-12T11:05:00Z',
      isEdited: true,
      reactions: {
        LIKE: 6,
        HELPFUL: 4
      }
    },
    {
      id: '2005',
      threadId: '201',
      content: 'Druid here! Shape-shifting is so much fun, and I love the versatility of being able to tank, heal, or deal damage depending on what the situation calls for.',
      author: 'NatureGuardian',
      createdAt: '2023-09-13T08:20:00Z',
      isEdited: false,
      reactions: {
        LIKE: 9,
        HELPFUL: 3
      }
    }
  ]
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getInitials = (name) => {
  return name.split(' ').map(part => part[0]).join('').toUpperCase();
};

const TavernThread = () => {
  const { threadId } = useParams();
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newPostContent, setNewPostContent] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 10;
  const [replyTo, setReplyTo] = useState(null);

  useEffect(() => {
    checkUser();
  }, []);

  async function checkUser() {
    try {
      const authenticatedUser = await getCurrentUser();
      setUser(authenticatedUser);
    } catch (err) {
      console.log('Error getting current user:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  const handleReaction = (postId, reactionType) => {
    console.log(`Reaction ${reactionType} to post ${postId}`);
    // This would be replaced with actual API call to add/remove reaction
  };

  const handleNewPost = (e) => {
    e.preventDefault();
    console.log('New post:', newPostContent, 'in reply to:', replyTo);
    // This would be replaced with actual API call to create a new post
    setNewPostContent('');
    setShowPreview(false);
    setReplyTo(null);
  };

  const handleReply = (postId, authorName) => {
    setReplyTo({ postId, authorName });
    // Scroll to reply form
    const replyForm = document.getElementById('post-reply-form');
    if (replyForm) {
      replyForm.scrollIntoView({ behavior: 'smooth' });
      // Focus on the textarea
      const textarea = replyForm.querySelector('textarea');
      if (textarea) {
        textarea.focus();
        // If replying to specific post, add a quote
        if (postId) {
          const post = findPostById(postId);
          if (post) {
            setNewPostContent(`@${authorName} said:\n> ${post.content.split('\n')[0]}...\n\n`);
          }
        }
      }
    }
  };

  const findPostById = (postId) => {
    const posts = MOCK_POSTS[threadId] || [];
    return posts.find(post => post.id === postId);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const thread = MOCK_THREADS_DATA[threadId];
  if (!thread) {
    return (
      <div className="tavern-container">
        <div className="thread-detail">
          <div className="thread-detail__header">
            <h1 className="thread-detail__title">Thread Not Found</h1>
          </div>
          <div className="thread-detail__content">
            <p>The thread you're looking for doesn't exist or has been removed.</p>
            <div className="tavern-actions" style={{ marginTop: '1rem' }}>
              <button 
                className="tavern-action-button"
                onClick={() => navigate('/tavern')}
              >
                Back to Tavern
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const posts = MOCK_POSTS[threadId] || [];
  
  // Pagination
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = posts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(posts.length / postsPerPage);

  return (
    <div className="tavern-container">
      <header className="tavern-header">
        <div className="tavern-header__content">
          <Link 
            to={`/tavern/category/${thread.categoryId}`}
            className="category-badge"
            style={{ 
              display: 'inline-block', 
              background: 'var(--color-primary)', 
              color: 'var(--color-text-light)',
              padding: '0.25rem 0.75rem',
              borderRadius: 'var(--border-radius)',
              marginBottom: 'var(--spacing-sm)',
              textDecoration: 'none'
            }}
          >
            {thread.categoryName}
          </Link>
          <h1 className="tavern-title">{thread.title}</h1>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: 'var(--spacing-md)', 
            color: 'var(--color-text-light)',
            marginTop: 'var(--spacing-sm)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <User size={16} style={{ marginRight: 'var(--spacing-xs)' }} />
              {thread.author}
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Clock size={16} style={{ marginRight: 'var(--spacing-xs)' }} />
              {formatDate(thread.createdAt)}
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <MessageCircle size={16} style={{ marginRight: 'var(--spacing-xs)' }} />
              {posts.length} {posts.length === 1 ? 'Reply' : 'Replies'}
            </div>
          </div>
        </div>
      </header>

      <main>
        <div className="post-list">
          {currentPosts.map((post, index) => (
            <div key={post.id} id={`post-${post.id}`} className="post-item">
              <div className="post-item__header">
                <div className="post-item__author">
                  <div className="post-item__author-avatar">
                    {getInitials(post.author)}
                  </div>
                  <div>
                    <div className="post-item__author-name">{post.author}</div>
                    <div className="post-item__meta">
                      {index === 0 ? 'Original Poster' : `Reply #${index}`}
                    </div>
                  </div>
                </div>
                <div className="post-item__date">
                  <Clock size={14} />
                  {formatDate(post.createdAt)}
                  {post.isEdited && <span className="post-item__edited">(edited)</span>}
                </div>
              </div>
              <div className="post-item__content">{post.content.split('\n').map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  <br />
                </React.Fragment>
              ))}</div>
              <div className="post-item__actions">
                <div className="post-item__reactions">
                  {Object.entries(post.reactions || {}).map(([type, count]) => (
                    <button 
                      key={type} 
                      className="post-reaction"
                      onClick={() => handleReaction(post.id, type)}
                    >
                      {type === 'LIKE' && <ThumbsUp size={14} />}
                      {type === 'HELPFUL' && <Award size={14} />}
                      {type === 'THANKS' && '🙏'}
                      <span className="post-reaction__count">{count}</span>
                    </button>
                  ))}
                </div>
                <div className="post-item__buttons">
                  <button className="post-button" onClick={() => handleReply(post.id, post.author)}>
                    <Reply size={14} />
                    Reply
                  </button>
                  {user && user.username === post.author && (
                    <>
                      <button className="post-button">
                        <Edit size={14} />
                        Edit
                      </button>
                      <button className="post-button">
                        <Trash size={14} />
                        Delete
                      </button>
                    </>
                  )}
                  <button className="post-button">
                    <Flag size={14} />
                    Report
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button 
              className={`pagination__item pagination__item--prev ${currentPage === 1 ? 'pagination__item--disabled' : ''}`}
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              «
            </button>
            
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index}
                className={`pagination__item ${currentPage === index + 1 ? 'pagination__item--active' : ''}`}
                onClick={() => setCurrentPage(index + 1)}
              >
                {index + 1}
              </button>
            ))}
            
            <button 
              className={`pagination__item pagination__item--next ${currentPage === totalPages ? 'pagination__item--disabled' : ''}`}
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              »
            </button>
          </div>
        )}
        
        {!thread.isLocked && user && (
          <form id="post-reply-form" className="post-reply-form" onSubmit={handleNewPost}>
            <div className="post-reply-form__header">
              <h2 className="post-reply-form__title">
                {replyTo 
                  ? `Reply to ${replyTo.authorName}`
                  : 'Post a Reply'
                }
              </h2>
              {replyTo && (
                <button 
                  type="button" 
                  className="post-button"
                  onClick={() => {
                    setReplyTo(null);
                    setNewPostContent('');
                  }}
                  style={{ marginLeft: 'var(--spacing-sm)' }}
                >
                  Cancel Reply
                </button>
              )}
            </div>
            {showPreview && (
              <div className="post-reply-form__preview">
                <h3 className="post-reply-form__preview-title">Preview</h3>
                <div className="post-reply-form__preview-content">
                  {newPostContent.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      <br />
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
            <textarea 
              className="post-reply-form__content"
              value={newPostContent}
              onChange={(e) => setNewPostContent(e.target.value)}
              placeholder="Write your reply..."
              required
            />
            <div className="post-reply-form__actions">
              <button 
                type="button" 
                className="tavern-action-button"
                onClick={() => setShowPreview(!showPreview)}
              >
                {showPreview ? 'Hide Preview' : 'Preview'}
              </button>
              <button type="submit" className="tavern-action-button">
                Post Reply
              </button>
            </div>
          </form>
        )}
        
        {thread.isLocked && (
          <div style={{ 
            marginTop: 'var(--spacing-lg)',
            padding: 'var(--spacing-md)',
            backgroundColor: 'var(--color-background-light)',
            borderRadius: 'var(--border-radius)',
            textAlign: 'center'
          }}>
            <p style={{ marginBottom: 'var(--spacing-sm)' }}>
              <span style={{ fontSize: '1.5rem', marginRight: 'var(--spacing-xs)' }}>🔒</span>
              This thread has been locked and is no longer accepting new replies.
            </p>
          </div>
        )}
        
        <div className="tavern-actions" style={{ marginTop: '2rem' }}>
          <button 
            className="tavern-action-button"
            onClick={() => navigate(`/tavern/category/${thread.categoryId}`)}
          >
            <ArrowLeft size={16} />
            Back to {thread.categoryName}
          </button>
        </div>
      </main>
    </div>
  );
};

export default TavernThread;